@use '@design/design-tokens/typography/typography' as *;
@use '@design/design-tokens/border-radius/border-radius' as *;
@use '@design/design-tokens/decorative-color/decorative-color' as *;
@use '@design/components/custom-scroll/custom-scroll' as *;


.wrapper {
  position: relative;
  flex: 1;
  min-width: 0;

  &.withPlaceholderGrid {
    display: grid;
    width: 100%;
  }
}

// Layout-neutral wrapper that lets each dropdown row own its mousedown handling.
.rowPressTarget {
  display: contents;
}

.popupList {
  grid-area: 1 / 1;
  min-width: 0;

  display: flex;
  flex-direction: column;

  [data-dropdown-list] {
    padding: 6px;
    background-color: var(--background-soft);
  }
}

.editor {
  @include custom-scroll-minimal;
  @include typography-v2-body-base;

  display: block;
  flex: 1;
  width: 100%;

  // Room for unresolved-mention corner badges that sit above the dashed box,
  // and for the selected-chip box-shadow ring below — neither affects layout
  // size, so without this padding `overflow-y: auto` clips them at the edge.
  padding-top: 4px;
  padding-bottom: 2px;
  color: var(--text-default);
  caret-color: var(--text-primary);
  outline: none;
  white-space: pre-wrap;
  overflow-wrap: break-word;

  max-height: 144px;
  overflow-y: auto;

  &[data-disabled='true'] {
    pointer-events: none;
    opacity: 0.6;
  }
}

.placeholder {
  grid-area: 1 / 1;
  align-self: start;
  z-index: 0;
  pointer-events: none;
  color: var(--text-disabled);
  @include typography-v2-body-base;

  padding-top: 4px;
  padding-bottom: 2px;

  white-space: normal;
  overflow-wrap: break-word;
}

.chip {
  position: relative;
  z-index: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 1px;
  vertical-align: middle;
  border-radius: 9999px;

  padding: 4px 8px;
  user-select: none;
  @include typography-v2-body-xs;

  &::before {
    content: '';
    position: absolute;
    inset: 1px 0;
    z-index: -1;
    border-radius: 9999px;
    outline: 1px solid transparent;
    outline-offset: -1px;
  }

  &:hover::before {
    inset: 0;
  }

  &.chipSelected {
    box-shadow: 0 0 0 2px currentColor;
  }
}

.chipEntity {
  @include decorative-pill-color('amber');
}

.chipTheme {
  @include decorative-pill-color('indigo');
}

.chipWatchlist {
  color: var(--text-default);

  &::before,
  &:hover::before {
    inset: 1px 0;
    background-color: var(--surface-soft);
    outline-color: var(--border-soft);
  }
}

.chipInsight {
  @include decorative-pill-color('teal');
}

.chipInsightScenario {
  @include decorative-pill-color('indigo');
}

.chipInsightCatalystCompany {
  @include decorative-pill-color('lime');
}

.chipInsightCatalystMarket {
  @include decorative-pill-color('cyan');
}

.chipInsightEarningsPreview {
  @include decorative-pill-color('purple');
}

.chipInsightEarningsRecap {
  @include decorative-pill-color('teal');
}

.chipAvatar {
  width: 16px;
  height: 16px;
  @include border-radius-full;
  flex: none;

  background-color: var(--surface-inverse);
}

.chipThemeAvatar {
  width: 16px;
  height: 16px;
  @include border-radius-full;
  flex: none;
}

.chipAvatarInitial {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  @include border-radius-full;
  @include typography-v2-body-xxs;

  font-weight: 500;
  font-style: normal;
  line-height: 1;
  text-transform: uppercase;
}

.chipAvatarInitialEntity {
  background-color: var(--surface-default);
  color: var(--text-default);
}

.chipLabel {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

.chipLabelPreview {
  display: block;
  overflow-wrap: break-word;
}

.chipGlyph {
  fill: currentColor;
  opacity: 0.9;
  flex: none;
}

.chipWatchlistGlyphWrap {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  @include border-radius-full;
  background-color: var(--surface-active);
}

.unresolvedMention {
  position: relative;
  z-index: 0;
  display: inline-flex;
  align-items: center;
  margin: 0 1px;
  padding: 2px 4px;
  vertical-align: middle;
  overflow: visible;
  border: 1px dashed var(--border-decorative-amber);
  border-radius: 2px;
  color: var(--text-default);
  cursor: pointer;
  user-select: none;
  @include typography-v2-body-xs;

  &.chipSelected {
    box-shadow: 0 0 0 2px var(--border-decorative-amber);
  }
}

.unresolvedBadge {
  position: absolute;
  top: -5px;
  right: -5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  @include border-radius-full;
  background-color: var(--border-decorative-amber);
  pointer-events: none;

  &::after {
    content: '?';
    color: var(--text-inverse);
    font-size: 8px;
    font-weight: 700;
    line-height: 1;

    // Optical center — default glyph sits slightly high in the circle.
    transform: translateY(0.5px);
  }
}

.chipPreviewBoundary {
  display: contents;
}

.unresolvedTooltipAnchor {
  position: fixed;
  pointer-events: none;
}
